import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Comic } from '../../types';
import { getImageUrl } from '../../api';
import { useComicsData } from '../../contexts/ComicsDataContext';
import { XMarkIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';

interface ComicAutocompleteProps {
  selectedComics: Comic[];
  onComicsChange: (comics: Comic[]) => void;
  placeholder?: string;
}

const ComicAutocomplete: React.FC<ComicAutocompleteProps> = ({
  selectedComics,
  onComicsChange,
  placeholder = "Search comics..."
}) => {
  const { t } = useTranslation();
  const { comics: allComics, loading: isLoading } = useComicsData();
  const [query, setQuery] = useState('');
  const [filteredComics, setFilteredComics] = useState<Comic[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });

  // Comics are loaded from shared context, no need for separate API call

  useEffect(() => {
    if (query.length > 0) {
      const filtered = allComics.filter(comic =>
        comic.title.toLowerCase().includes(query.toLowerCase()) ||
        (comic.series && comic.series.toLowerCase().includes(query.toLowerCase())) ||
        false
      ).filter(comic => !selectedComics.some(selected => selected.id === comic.id));
      
      setFilteredComics(filtered.slice(0, 20)); // Limit to 20 results
    } else {
      setFilteredComics([]);
    }
  }, [query, allComics, selectedComics]);

  const handleSelectComic = (comic: Comic) => {
    onComicsChange([...selectedComics, comic]);
    setQuery('');
    // Keep dropdown open after selection
  };

  const handleRemoveComic = (comicId: number) => {
    onComicsChange(selectedComics.filter(comic => comic.id !== comicId));
  };

  const handleInputFocus = () => {
    setIsOpen(true);
    updateDropdownPosition();
  };

  const updateDropdownPosition = () => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width
      });
    }
  };

  const handleInputBlur = (e: React.FocusEvent) => {
    // Only close if the focus is moving outside the component
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setTimeout(() => setIsOpen(false), 150);
    }
  };

  return (
    <div className="space-y-3">
      {/* Selected Comics Display */}
      {selectedComics.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedComics.map((comic) => (
            <div
              key={comic.id}
              className="flex items-center space-x-2 bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-200 px-3 py-1 rounded-full text-sm"
            >
              <span className="font-medium">{comic.title}</span>
              {comic.series && (
                <span className="text-blue-600 dark:text-blue-300">({comic.series})</span>
              )}
              <button
                type="button"
                onClick={() => handleRemoveComic(comic.id)}
                className="text-blue-600 dark:text-blue-300 hover:text-blue-800 dark:hover:text-blue-100 transition-colors"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Search Input */}
      <div className="relative">
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            placeholder={placeholder}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {/* Dropdown Portal */}
        {isOpen && createPortal(
          <div 
            className="fixed z-[9999] bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto"
            style={{
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              width: dropdownPosition.width
            }}
          >
              {isLoading ? (
              <div className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400">{t('common.loadingComics')}</div>
            ) : query.length > 0 ? (
              filteredComics.length > 0 ? (
                filteredComics.map((comic) => (
                  <button
                    key={comic.id}
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleSelectComic(comic);
                    }}
                    className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 focus:bg-gray-100 focus:outline-none transition-colors"
                  >
                    <div className="flex items-center space-x-3">
                      {comic.cover_image && (
                        <img
                          src={getImageUrl(comic.cover_image)}
                          alt={comic.title}
                          className="w-8 h-12 object-cover rounded"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                          {comic.title}
                        </p>
                        {comic.series && (
                          <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                            {comic.series}
                          </p>
                        )}
                        <div className="flex items-center space-x-2 text-xs text-gray-400">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            comic.status === 'owned' ? 'bg-green-100 text-green-800' :
                            comic.status === 'wishlist' ? 'bg-yellow-100 text-yellow-800' :
                            comic.status === 'ordered' ? 'bg-blue-100 text-blue-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {comic.status}
                          </span>
                        </div>
                      </div>
                    </div>
                  </button>
                ))
              ) : (
                <div className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400">
                  {t('common.noComicsFound', { query })}
                </div>
              )
            ) : (
              <div className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400">
                {t('placeholder.startTypingToSearchComics')}
              </div>
            )}
          </div>,
          document.body
        )}
      </div>

      {/* Comics Count */}
      <div className="text-sm text-gray-500 dark:text-gray-400">
        {t('common.comicsSelected', { count: selectedComics.length, defaultValue_one: '{{count}} comic selected', defaultValue_other: '{{count}} comics selected' })}
      </div>
    </div>
  );
};

export default ComicAutocomplete;